import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    MenuItem,
    Select,
    useMediaQuery,
    Card,
    Stack,
    CardContent,
    FormControl,
    CircularProgress,
    Alert,
    Chip,
    OutlinedInput,
    Checkbox,
    ListItemText,
    Skeleton
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/Upload';
import { logBookService, LogBookFilters, LogBookEntry, Occupant } from '../../services/logBookService';
import { locationService, Ward } from '../../services/locationService';
import ExportLogBookModal from './components/ExportLogBookModal';
import OccupantDetailsModal from './components/OccupantDetailsModal';
import { DataTable, DataTableColumn } from '../../components/DataTable';

const LogBook = () => {
    const isMobile = useMediaQuery('(max-width:600px)');
    
    // Generate financial year options (past 5 years + current year + 5 future years)
    const currentYear = new Date().getFullYear();
    const financialYearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    
    // Status options
    const statusOptions = ['Pending', 'Review Passed', 'Approved', 'Review Failed', 'Rejected'];
    
    // State management
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logBookData, setLogBookData] = useState<LogBookEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [filters, setFilters] = useState<LogBookFilters>({
        ward_no: null,
        financial_year: null,
        status: null,
        page: 1,
        limit: 10
    });
    const [exportModalOpen, setExportModalOpen] = useState(false);
    
    // State for wards data
    const [wards, setWards] = useState<Ward[]>([]);
    const [wardsLoading, setWardsLoading] = useState(true);

    // State for occupant details modal
    const [occupantDetailsModalOpen, setOccupantDetailsModalOpen] = useState(false);
    const [selectedOccupants, setSelectedOccupants] = useState<Occupant[]>([]);
    const [selectedIndigentName, setSelectedIndigentName] = useState('');
    const [selectedIndigentNumber, setSelectedIndigentNumber] = useState('');

    // Fetch wards data
    const fetchWards = async () => {
        try {
            setWardsLoading(true);
            const wardsData = await locationService.getWards();
            setWards(wardsData);
        } catch (err) {
            console.error('Error fetching wards:', err);
            setError('Failed to load wards data');
        } finally {
            setWardsLoading(false);
        }
    };

    // Fetch log book data
    const fetchLogBookData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await logBookService.getLogBookEntries({
                ...filters,
                page: currentPage,
                limit
            });
            
            setLogBookData(response.data);
            setTotal(response.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch log book data');
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount and when filters/page changes
    useEffect(() => {
        fetchWards();
    }, []);

    useEffect(() => {
        fetchLogBookData();
    }, [currentPage, filters]);

    // Handle filter changes
    const handleFilterChange = (filterName: keyof LogBookFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
        setCurrentPage(1); // Reset to first page when filters change
    };

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Handle export
    const handleExport = () => {
        setExportModalOpen(true);
    };

    // Handle occupant details modal
    const handleOccupantDetails = (occupants: Occupant[], indigentName: string, indigentNumber: string) => {
        setSelectedOccupants(occupants);
        setSelectedIndigentName(indigentName);
        setSelectedIndigentNumber(indigentNumber);
        setOccupantDetailsModalOpen(true);
    };

    return (
        <div style={{
            backgroundColor: '#FEFBFF',
            minHeight: window.innerWidth < 600 ? 'calc(100vh - 96px)' : '100vh',
            position: window.innerWidth < 600 ? 'absolute' : 'relative',
            top: window.innerWidth < 600 ? '86px' : 'auto',
            left: window.innerWidth < 600 ? '0' : 'auto',
            right: window.innerWidth < 600 ? '0' : 'auto',
            bottom: window.innerWidth < 600 ? '0' : 'auto',
            padding: '16px',
            paddingBottom: window.innerWidth < 600 ? '32px' : '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: window.innerWidth < 600 ? '100vw' : '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden'
        }}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: isMobile?'1px':'24px',
                paddingBottom: window.innerWidth < 600 ? '24px' : '16px',
                width: '100%',
                maxWidth: '100%',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: window.innerWidth < 600 ? '16px' : '0'
            }}>
                <Box p={2}>
                    {/* Row 1: Title + Export */}
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        flexWrap="wrap"
                    >
                        <Typography fontWeight={600} fontSize="22px" fontFamily="Poppins, sans-serif" color='#132F5F'>
                            Indigent Register
                        </Typography>

                        <Stack alignItems="center" spacing={0.5}>
                            <Button
                                startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 28 }} />}
                                onClick={handleExport}
                                sx={{
                                    bgcolor: '#203A6B',
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1.2,
                                    fontSize: '14px',
                                    borderRadius: '12px',
                                    color:"#fff",
                                    '&:hover': {
                                        bgcolor: '#1a2e56',
                                    },
                                    height:isMobile?"auto":"56px",
                                    width:isMobile?"auto":"128px"
                                }}
                            >
                                Export
                            </Button>
                            <Typography
                                variant="caption"
                                sx={{ color: '#6f6f6f', textAlign: 'center', mt: -0.5 }}
                            >
                                Export data in pdf or csv
                            </Typography>
                        </Stack>
                    </Box>

                    {/* Error Alert */}
                    {error && (
                        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Filters */}
                    <Box
                        display='flex'
                        justifyContent='space-between'
                        flexWrap='wrap'
                        alignItems='center'
                        mb={2}
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        gap={{ xs: 2, sm: 3 }} 
                    >
                        <Box display='flex' gap={6} flexWrap='wrap' color="#49454F" justifyContent="space-between">
                            <Box>
                                <Typography fontSize='14px' fontWeight={600} mb={0.5}>
                                    Ward Number
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                        multiple
                                        value={filters.ward_no || []}
                                        onChange={(e) => handleFilterChange('ward_no', e.target.value)}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) return 'All';
                                            return selected.join(', ');
                                        }}
                                        displayEmpty
                                        disabled={wardsLoading}
                                        sx={{
                                            borderRadius: 1,
                                            bgcolor: '#fff',
                                            fontSize: '14px',
                                            height:"40px"
                                        }}
                                    >
                                        {wards.map((ward) => (
                                            <MenuItem key={ward.id} value={ward.ward_number}>
                                                <Checkbox checked={(filters.ward_no || []).indexOf(ward.ward_number) > -1} />
                                                <ListItemText primary={ward.ward_number} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box>
                                <Typography fontSize='14px' fontWeight={600} mb={0.5}>
                                    Status
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                        multiple
                                        value={filters.status || []}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) return 'All';
                                            return selected.map(apiStatus => {
                                                // Map API status back to UI labels
                                                switch (apiStatus) {
                                                    case 'pending': return 'Pending';
                                                    case 'reviewed': return 'Review Passed';
                                                    case 'approved': return 'Approved';
                                                    case 'failed': return 'Review Failed';
                                                    case 'rejected': return 'Rejected';
                                                    default: return apiStatus;
                                                }
                                            }).join(', ');
                                        }}
                                        displayEmpty
                                        sx={{
                                            borderRadius: 1,
                                            bgcolor: '#fff',
                                            fontSize: '14px',
                                            height:"40px"
                                        }}
                                    >
                                        {statusOptions.map((status) => (
                                            <MenuItem key={status} value={logBookService.mapUIToAPIStatus(status)}>
                                                <Checkbox checked={(filters.status || []).indexOf(logBookService.mapUIToAPIStatus(status)) > -1} />
                                                <ListItemText primary={status} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box>
                                <Typography fontSize='14px' fontWeight={600} mb={0.5}>
                                    Financial Year
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <Select
                                        multiple
                                        value={filters.financial_year || []}
                                        onChange={(e) => handleFilterChange('financial_year', e.target.value)}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (!selected || selected.length === 0) return 'All';
                                            return selected.join(', ');
                                        }}
                                        displayEmpty
                                        sx={{
                                            borderRadius: 1,
                                            bgcolor: '#fff',
                                            fontSize: '14px',
                                            height:"40px"
                                        }}
                                    >
                                        {financialYearOptions.map(year => (
                                            <MenuItem key={year} value={year}>
                                                <Checkbox checked={(filters.financial_year || []).indexOf(year) > -1} />
                                                <ListItemText primary={year.toString()} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>

                    {/* Table - Now using DataTable for both mobile and desktop */}
                    <Box mt={2}>
                        <DataTable
                            columns={[
                                { key: 'indigent_number', label: 'ID No', width: '100px' },
                                { key: 'erf_no', label: 'Erf No', width: '120px' },
                                { key: 'electricity_meter_no', label: 'Electricity Meter No', width: '150px' },
                                { key: 'account_number', label: 'Account Number', width: '140px' },
                                { key: 'sa_id', label: 'SA ID', width: '120px' },
                                { key: 'name', label: 'Name', width: '180px', render: (value, row) => row.full_name || logBookService.getFullName(row.name, row.surname) },
                                { key: 'ward_no', label: 'Ward No', width: '100px' },
                                { key: 'services', label: 'Service', width: '120px', render: (value, row) => logBookService.formatServices(row.services) },
                                { key: 'status', label: 'Status', width: '120px', render: (value, row) => logBookService.mapStatusToUI(row.status) },
                                { key: 'review_passed', label: 'Review Passed', width: '120px', render: (value) => value ? 'Yes' : 'No' },
                                { key: 'approval_date', label: 'Approval Date', width: '130px', render: (value) => logBookService.formatDate(value) },
                                { key: 'request_type', label: 'Type', width: '100px' },
                                { key: 'cell_number', label: 'Cell Number', width: '130px' },
                                { 
                                    key: 'occupants', 
                                    label: 'Occupants', 
                                    width: '120px',
                                    render: (value, row) => {
                                        if (row.occupants && row.occupants.length > 0) {
                                            return (
                                                <Chip
                                                    onClick={() => handleOccupantDetails(
                                                        row.occupants,
                                                        row.full_name || logBookService.getFullName(row.name, row.surname),
                                                        row.indigent_number
                                                    )}
                                                    label={`${row.occupants.length} occupant${row.occupants.length > 1 ? 's' : ''}`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#E3F2FD',
                                                        color: '#1976D2',
                                                        fontSize: '12px',
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            bgcolor: '#BBDEFB',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                />
                                            );
                                        } else {
                                            return (
                                                <Chip
                                                    label="0 occupants"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#F5F5F5',
                                                        color: '#666',
                                                        fontSize: '12px',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            );
                                        }
                                    }
                                },
                                { key: 'financial_year', label: 'Financial Year', width: '120px', render: (value) => value?.toString() || '—' },
                            ]}
                            data={logBookData}
                            loading={loading}
                            total={total}
                            currentPage={currentPage}
                            limit={limit}
                            onPageChange={(page) => setCurrentPage(page)}
                            emptyMessage="No log book entries found"
                        />
                    </Box>

                    {/* COMMENTED OUT: Mobile Cards View - Preserved for future reference if needed
                    {isMobile ? (
                        // Mobile Cards View
                        <Stack spacing={2} mt={2}>
                            {loading ? (
                                // Mobile Loading State
                                Array.from({ length: 3 }).map((_, index) => (
                                    <Card
                                        key={index}
                                        sx={{
                                            borderRadius: '16px',
                                            boxShadow: 'none',
                                            border: '1px solid #E0E0E0',
                                            p: 2,
                                            backgroundColor: "#FAF9F9"
                                        }}
                                    >
                                        <CardContent sx={{ p: 0 }}>
                                            {Array.from({ length: 8 }).map((_, itemIndex) => (
                                                <Box key={itemIndex} display='flex' justifyContent='space-between' mb={1}>
                                                    <Skeleton variant="text" width="40%" height={20} />
                                                    <Skeleton variant="text" width="50%" height={20} />
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ))
                            ) : logBookData.length === 0 ? (
                                <Typography textAlign="center" color="text.secondary" py={4}>
                                    No log book entries found
                                </Typography>
                            ) : (
                                logBookData.map((entry, i) => (
                                    <Card
                                        key={entry.id}
                                        sx={{
                                            borderRadius: '16px',
                                            boxShadow: 'none',
                                            border: '1px solid #E0E0E0',
                                            p: 2,
                                            backgroundColor:"#FAF9F9"
                                        }}
                                    >
                                        <CardContent sx={{ p: 0 }}>
                                            {[
                                                { label: 'ID No', value: entry.indigent_number },
                                                { label: 'Erf No', value: entry.erf_no },
                                                { label: 'Electricity Meter No', value: entry.electricity_meter_no },
                                                { label: 'Account Number', value: entry.account_number },
                                                { label: 'Name', value: entry.full_name || logBookService.getFullName(entry.name, entry.surname) },
                                                { label: 'Ward No', value: entry.ward_no },
                                                { label: 'Service', value: logBookService.formatServices(entry.services) },
                                                { label: 'Status', value: logBookService.mapStatusToUI(entry.status) },
                                                { label: 'Review Passed', value: entry.review_passed ? 'Yes' : 'No' },
                                                { label: 'Approval Date', value: logBookService.formatDate(entry.approval_date) },
                                                { label: 'Type', value: entry.request_type },
                                                { label: 'Cell Number', value: entry.cell_number },
                                                { label: 'Occupants', value: entry.occupants?.length || 0 },
                                                { label: 'Financial Year', value: entry.financial_year?.toString() || '' },
                                            ].map((item, idx) => (
                                                <Box key={`${entry.id}-${item.label}`} display='flex' justifyContent='space-between' mb={1}>
                                                    <Typography
                                                        fontWeight={600}
                                                        fontSize='13px'
                                                        color='text.secondary'
                                                        width='50%'
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                    <Box width='50%' textAlign='right' display='flex' alignItems='center' justifyContent='flex-end'>
                                                        {item.label === 'Occupants' ? (
                                                            entry.occupants && entry.occupants.length > 0 ? (
                                                                <Chip
                                                                    onClick={() => handleOccupantDetails(
                                                                        entry.occupants,
                                                                        entry.full_name || logBookService.getFullName(entry.name, entry.surname),
                                                                        entry.indigent_number
                                                                    )}
                                                                    label={`${entry.occupants.length} occupant${entry.occupants.length > 1 ? 's' : ''}`}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#E3F2FD',
                                                                        color: '#1976D2',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500,
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            bgcolor: '#BBDEFB',
                                                                            transform: 'translateY(-1px)',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                        },
                                                                        transition: 'all 0.2s ease'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label="0 occupants"
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#F5F5F5',
                                                                        color: '#666',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500
                                                                    }}
                                                                />
                                                            )
                                                        ) : (
                                                            <Typography fontSize='13px'>
                                                                {item.value || '—'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </Stack>
                    ) : (
                        // Desktop Table View
                        <Box mt={2}>
                            <DataTable
                                columns={[
                                    { key: 'indigent_number', label: 'ID No', width: '100px' },
                                    { key: 'erf_no', label: 'Erf No', width: '120px' },
                                    { key: 'electricity_meter_no', label: 'Electricity Meter No', width: '150px' },
                                    { key: 'account_number', label: 'Account Number', width: '140px' },
                                    { key: 'name', label: 'Name', width: '150px', render: (value, row) => row.full_name || logBookService.getFullName(row.name, row.surname) },
                                    { key: 'ward_no', label: 'Ward No', width: '100px' },
                                    { key: 'services', label: 'Service', width: '120px', render: (value, row) => logBookService.formatServices(row.services) },
                                    { key: 'status', label: 'Status', width: '120px', render: (value, row) => logBookService.mapStatusToUI(row.status) },
                                    { key: 'review_passed', label: 'Review Passed', width: '120px', render: (value) => value ? 'Yes' : 'No' },
                                    { key: 'approval_date', label: 'Approval Date', width: '130px', render: (value) => logBookService.formatDate(value) },
                                    { key: 'request_type', label: 'Type', width: '100px' },
                                    { key: 'cell_number', label: 'Cell Number', width: '130px' },
                                    { 
                                        key: 'occupants', 
                                        label: 'Occupants', 
                                        width: '120px',
                                        render: (value, row) => {
                                            if (row.occupants && row.occupants.length > 0) {
                                                return (
                                                    <Chip
                                                        onClick={() => handleOccupantDetails(
                                                            row.occupants,
                                                            row.full_name || logBookService.getFullName(row.name, row.surname),
                                                            row.indigent_number
                                                        )}
                                                        label={`${row.occupants.length} occupant${row.occupants.length > 1 ? 's' : ''}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#E3F2FD',
                                                            color: '#1976D2',
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                bgcolor: '#BBDEFB',
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                            },
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    />
                                                );
                                            } else {
                                                return (
                                                    <Chip
                                                        label="0 occupants"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: '#F5F5F5',
                                                            color: '#666',
                                                            fontSize: '12px',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                );
                                            }
                                        }
                                    },
                                    { key: 'financial_year', label: 'Financial Year', width: '120px', render: (value) => value?.toString() || '—' },
                                ]}
                                data={logBookData}
                                loading={loading}
                                total={total}
                                currentPage={currentPage}
                                limit={limit}
                                onPageChange={(page) => setCurrentPage(page)}
                                emptyMessage="No log book entries found"
                            />
                        </Box>
                    )}
                    */}

                    {/* Pagination - Remove this section since it's now handled by DataTable */}
                    {/* {!loading && total > 0 && (
                        <Box mt={2} display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography fontSize='14px'>
                                Showing <b>{((currentPage - 1) * limit) + 1}</b> to <b>{Math.min(currentPage * limit, total)}</b> of <b>{total}</b> results
                            </Typography>
                            <Pagination 
                                count={Math.ceil(total / limit)} 
                                page={currentPage} 
                                onChange={handlePageChange}
                            />
                        </Box>
                    )} */}
                </Box>
            </div>
            <ExportLogBookModal open={exportModalOpen} onClose={() => setExportModalOpen(false)} />
            <OccupantDetailsModal
                open={occupantDetailsModalOpen}
                onClose={() => setOccupantDetailsModalOpen(false)}
                occupants={selectedOccupants}
                indigentName={selectedIndigentName}
                indigentNumber={selectedIndigentNumber}
            />
        </div>
    );
};

export default LogBook;
